/**
 * AI Commentary block + parsed 3-block renderer (FAIT / EXPLICATION /
 * RECOMMANDATION). Extracted from AnalyticsCalls.jsx (iter29 refactor).
 */
import { Loader2, RefreshCw, Sparkles } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { api } from "../../lib/api";

function CommentarySections({ text }) {
    // The Claude prompt now returns 3 explicit blocks — Fait, Explication,
    // Recommandation — separated by blank lines. We split on those blocks
    // and render each with its own tinted label so the reader immediately
    // sees the structure. Fallback: if the model returned a single blob,
    // we render it as-is with preserved newlines.
    const blocks = useMemo(() => {
        if (!text) return null;
        const map = { fait: "", explication: "", recommandation: "" };
        const patterns = [
            { key: "fait",           re: /(?:^|\n\s*)Fait\s*:\s*/i },
            { key: "explication",    re: /(?:^|\n\s*)Explication\s*:\s*/i },
            { key: "recommandation", re: /(?:^|\n\s*)Recommandation\s*:\s*/i },
        ];
        const hits = patterns
            .map((p) => ({ ...p, idx: text.search(p.re) }))
            .filter((h) => h.idx >= 0)
            .sort((a, b) => a.idx - b.idx);
        if (hits.length === 0) {
            return null;
        }
        for (let i = 0; i < hits.length; i++) {
            const start = hits[i].idx;
            const nextStart = i + 1 < hits.length ? hits[i + 1].idx : text.length;
            const slice = text.slice(start, nextStart);
            const content = slice.replace(hits[i].re, "").trim();
            map[hits[i].key] = content;
        }
        return map;
    }, [text]);

    if (!text) return null;
    if (!blocks) {
        return (
            <div className="whitespace-pre-line" data-testid="ai-commentary-text">
                {text}
            </div>
        );
    }

    const rows = [
        { key: "fait", label: "Fait", tone: "bg-shariti-orange/15 text-shariti-orange-light" },
        { key: "explication", label: "Explication", tone: "bg-indigo-500/15 text-indigo-200" },
        { key: "recommandation", label: "Recommandation", tone: "bg-emerald-500/15 text-emerald-200" },
    ];

    return (
        <div className="space-y-3.5" data-testid="ai-commentary-text">
            {rows.map((r) => (
                blocks[r.key] ? (
                    <div key={r.key} className="grid grid-cols-[auto_1fr] gap-3 items-start">
                        <div className={`shrink-0 rounded-full px-2.5 py-1 text-[9.5px] font-black uppercase tracking-[0.16em] ${r.tone}`}>
                            {r.label}
                        </div>
                        <div className="text-slate-100">{blocks[r.key]}</div>
                    </div>
                ) : null
            ))}
        </div>
    );
}


export function AiCommentaryBlock({ scope, filters, testid }) {
    const [text, setText] = useState("");
    const [loading, setLoading] = useState(true);
    const [cached, setCached] = useState(false);

    const load = useCallback(async (forceRefresh = false) => {
        setLoading(true);
        try {
            const { data } = await api.get("/analytics/calls/commentary", {
                params: {
                    scope,
                    from: filters.from,
                    to: filters.to,
                    offer_id: filters.offer_id || undefined,
                    recruiter_id: filters.recruiter_id || undefined,
                    source: filters.source || undefined,
                    force_refresh: forceRefresh || undefined,
                },
            });
            setText(data.commentary || "");
            setCached(!!data.cached);
        } catch (_e) {
            setText("Le commentaire IA n'a pas pu être généré. Réessayez dans quelques secondes.");
        } finally {
            setLoading(false);
        }
    }, [scope, filters.from, filters.to, filters.offer_id, filters.recruiter_id, filters.source]);

    useEffect(() => { load(false); }, [load]);

    return (
        <div
            data-testid={testid}
            className="rounded-3xl bg-gradient-to-br from-shariti-black via-slate-900 to-slate-800 text-white p-6 md:p-8 relative overflow-hidden"
        >
            <div className="absolute -right-8 -top-8 h-40 w-40 rounded-full bg-shariti-orange/20 blur-3xl pointer-events-none" />
            <div className="relative flex items-start justify-between mb-4 gap-4">
                <div>
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-shariti-orange/20 text-shariti-orange-light text-[10px] font-black uppercase tracking-[0.2em]">
                        <Sparkles className="w-3 h-3" />
                        Analyse IA · Claude Sonnet 4.5
                    </div>
                </div>
                <button
                    type="button"
                    onClick={() => load(true)}
                    disabled={loading}
                    data-testid={`${testid}-refresh`}
                    title="Régénérer le commentaire"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-xs font-bold text-white transition disabled:opacity-60"
                >
                    {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                    Rafraîchir
                </button>
            </div>
            <div className="relative text-sm md:text-base leading-relaxed font-medium text-slate-100 min-h-[80px]">
                {loading && !text ? (
                    <div className="text-slate-400 italic">Génération du commentaire en cours…</div>
                ) : (
                    <CommentarySections text={text} />
                )}
            </div>
            {cached && (
                <div className="relative mt-3 text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                    Résultat servi depuis le cache · rafraîchi automatiquement toutes les 30 min
                </div>
            )}
        </div>
    );
}
